import React, {useMemo} from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  styled,
} from '@mui/material';
import {Body, Icon, designSystemToken} from '@lightricks/react-design-system';
import {DesignSystemIcon} from '@contentlab/types/designSystemIcon';

enum AccordionVariant {
  REGULAR = 'REGULAR',
  COMPACT = 'COMPACT',
  DISABLED = 'DISABLED',
}

const DEFAULT_HEIGHT = '56px';

interface StyledAccordionProps {
  accordionVariant: AccordionVariant;
}

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop: string) => prop !== 'accordionVariant',
})<StyledAccordionProps>(({accordionVariant}) => ({
  'backgroundColor': designSystemToken('semantic.bg.primary'),
  'borderRadius': '16px !important',
  'margin': '0 auto 12px auto',
  'border': `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  'boxShadow': 'none',
  '&.Mui-expanded': {
    margin: '0 auto 12px auto',
  },
  'width': accordionVariant === AccordionVariant.COMPACT ? '100%' : '90%',
  'opacity': accordionVariant === AccordionVariant.DISABLED ? 0.6 : 1,
  'pointerEvents':
    accordionVariant === AccordionVariant.DISABLED ? 'none' : 'auto',
  '&:before': {
    display: 'none',
  },
}));

const SectionHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '0px',
  width: '100%',
  justifyContent: 'space-between',
}));

const SectionHeaderContent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const DropdownArrow = styled(Icon, {
  shouldForwardProp: (prop: string) => prop !== 'expanded',
})<{expanded: boolean}>(({expanded}) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease-in-out',
  flexShrink: 0,
  marginLeft: 'auto',
  marginRight: '8px',
}));

const SectionContent = styled(Box)(() => ({
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingBottom: '16px',
  paddingTop: '16px',
}));

interface AccordionSectionProps {
  testID?: string;
  sectionKey: string;
  title: string;
  iconName: DesignSystemIcon;
  expanded: boolean;
  onToggle: (section: string, expanded: boolean) => void;
  children: React.ReactNode;
  variant?: 'default' | 'compact';
  disabled?: boolean;
  accordionHeight?: string;
  accordionSummaryStyle?: React.CSSProperties;
  accordionDetailsStyle?: React.CSSProperties;
}

function AccordionSection({
  testID = 'accordion-section',
  sectionKey,
  title,
  iconName,
  expanded,
  onToggle,
  children,
  variant = 'default',
  disabled = false,
  accordionHeight = DEFAULT_HEIGHT,
  accordionSummaryStyle = {},
  accordionDetailsStyle = {},
}: AccordionSectionProps) {
  const handleToggle = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (!disabled) {
      onToggle(sectionKey, isExpanded);
    }
  };

  const accordionVariant = useMemo(() => {
    if (disabled) return AccordionVariant.DISABLED;
    if (variant === 'compact') return AccordionVariant.COMPACT;
    return AccordionVariant.REGULAR;
  }, [disabled, variant]);

  return (
    <StyledAccordion
      data-testid={testID}
      expanded={expanded}
      onChange={handleToggle}
      accordionVariant={accordionVariant}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{height: accordionHeight, ...accordionSummaryStyle}}
      >
        <SectionHeader>
          <SectionHeaderContent>
            <Box
              sx={{
                backgroundColor: designSystemToken('semantic.bg.tertiary'),
                width: 32,
                height: 32,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon
                name={iconName}
                size="small"
                appearance="neutral"
                style={{width: '20px', height: '20px'}}
                color={designSystemToken('semantic.fg.primary')}
              />
            </Box>
            <Body size="lg">{title}</Body>
          </SectionHeaderContent>
          <DropdownArrow
            name="Actions-Arrow-Down-Mini"
            size="medium"
            appearance="neutral"
            expanded={expanded}
            color={designSystemToken('semantic.bg.alpha-secondary')}
          />
        </SectionHeader>
      </AccordionSummary>
      <AccordionDetails sx={{padding: '0px', ...accordionDetailsStyle}}>
        <SectionContent>{children}</SectionContent>
      </AccordionDetails>
    </StyledAccordion>
  );
}

export default AccordionSection;
